import { useTranslation } from 'react-i18next';
import { MapPinnedIcon } from 'lucide-react';
import { useYmaps } from '@noor/maps';
import {
  useCourierLocation,
  useCourierBatteryLocation,
  toLngLat,
  type OrderDetail,
} from '@/entities/order';
import { useOrderOffer } from '../model/use-order-offer';
import { MapCanvas } from './map-canvas';
import { OsmCanvas } from './osm-canvas';

interface OrderMapProps {
  order: OrderDetail;
  className?: string;
  provider?: 'yandex' | 'osm';
}

/**
 * Live order-tracking map (Yandex Maps v3). Renders origin/destination
 * placemarks + a road-snapped route polyline + a live courier marker polled
 * every 5s for stages 7–14. Coordinates are flipped from
 * the API `{lat,long}` to ymaps `[long,lat]`. Degrades to a styled placeholder
 * when the SDK is disabled (no API key) or still loading.
 */
export function OrderMap({ order, className, provider = 'yandex' }: OrderMapProps) {
  const { t } = useTranslation('orders');
  const { bindings, isDisabled } = useYmaps();

  const courierId = order.courier?.id ?? null;
  const courierLoc = useCourierLocation(order.id, courierId, order.stage);

  // Build [long, lat] route points from the order's origin/destination.
  const points: { coords: [number, number]; type: string; order: number }[] = [];
  for (const p of [
    ...(order.origin ?? []).map((point, index) => ({
      ...point,
      kind: 'origin',
      routeOrder: point.order ?? index * 2,
    })),
    ...(order.destination ?? []).map((point, index) => ({
      ...point,
      kind: 'destination',
      routeOrder: point.order ?? index * 2 + 1,
    })),
  ].sort((a, b) => a.routeOrder - b.routeOrder)) {
    const ll = toLngLat(p.location);
    if (ll) points.push({ coords: ll, type: p.kind, order: p.routeOrder });
  }

  const battery = useCourierBatteryLocation(order.id, courierId, order.stage);
  const offer = useOrderOffer(order.id, order.stage);
  const extraMarkers = [
    ...(battery.data?.location
      ? [
          {
            coords: [
              battery.data.location.long + 0.00001,
              battery.data.location.lat + 0.00001,
            ] as [number, number],
            kind: 'battery' as const,
            reportedAt: battery.data.last_reported_at ?? null,
            batteryPct:
              battery.data.battery_percentage == null
                ? null
                : Number(battery.data.battery_percentage),
          },
        ]
      : []),
    ...(offer
      ? [
          {
            coords: [offer.location.long, offer.location.lat] as [number, number],
            kind: 'offer' as const,
            reportedAt: null,
            courierId: offer.id,
          },
        ]
      : []),
  ];
  const courierCoords = toLngLat(courierLoc.data?.location);

  if (provider === 'osm' || isDisabled) {
    return (
      <OsmCanvas
        points={points}
        extraMarkers={extraMarkers}
        courierCoords={courierCoords ?? undefined}
        courierLastReportedAt={courierLoc.data?.last_reported_at ?? null}
        className={className}
      />
    );
  }

  if (isDisabled || !bindings) {
    return (
      <div
        className={`flex flex-col items-center justify-center gap-2 bg-muted/30 text-muted-foreground ${className ?? ''}`}
        data-testid="order-map-placeholder"
      >
        <MapPinnedIcon className="size-8 opacity-50" />
        <p className="px-4 text-center text-sm">
          {t(isDisabled ? 'detail.mapUnavailable' : 'loadingOptions')}
        </p>
      </div>
    );
  }

  return (
    <MapCanvas
      bindings={bindings}
      points={points}
      extraMarkers={extraMarkers}
      {...(courierCoords ? { courierCoords } : {})}
      courierLastReportedAt={courierLoc.data?.last_reported_at ?? null}
      className={className}
    />
  );
}
